import React, { useState, useEffect } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend,
  LineElement,
  PointElement
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import LZString from 'lz-string';

// Rejestracja komponentów Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement
);

const CurprLottoAnalyzer = () => {
  const [lottoData, setLottoData] = useState([]);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState('api');
  const [analysisRange, setAnalysisRange] = useState(100);
  const [csvFile, setCsvFile] = useState(null);
  const [curprScore, setCurprScore] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [fileName, setFileName] = useState('');
  const [showExplanations, setShowExplanations] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Hook do śledzenia rozmiaru okna
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Symulowane dane Lotto dla demonstracji
  const generateSimulatedData = (count) => {
    const data = [];
    for (let i = 0; i < count; i++) {
      const numbers = [];
      while (numbers.length < 6) {
        const num = Math.floor(Math.random() * 49) + 1;
        if (!numbers.includes(num)) {
          numbers.push(num);
        }
      }
      data.push({
        date: new Date(Date.now() - i * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        numbers: numbers.sort((a, b) => a - b)
      });
    }
    return data.reverse();
  };

  // Konwersja danych na bity
  const convertToBits = (data) => {
    let bits = '';
    data.forEach(draw => {
      const binaryVector = new Array(49).fill(0);
      draw.numbers.forEach(num => {
        binaryVector[num - 1] = 1;
      });
      bits += binaryVector.join('');
    });
    return bits;
  };

  // Test Monobit
  const monobitTest = (bits) => {
    const n = bits.length;
    const ones = (bits.match(/1/g) || []).length;
    const zeros = n - ones;
    const zScore = (ones - n/2) / Math.sqrt(n/4);
    return {
      test: 'Monobit',
      value: zScore.toFixed(3),
      interpretation: Math.abs(zScore) < 2 ? 'Rozkład jedynek/zer równy' : 
                     Math.abs(zScore) < 3 ? 'Lekkie odchylenie' : 'Silne odchylenie',
      status: Math.abs(zScore) < 2 ? '✅ OK' : Math.abs(zScore) < 3 ? '⚠️ Graniczny' : '❌ Odchylenie'
    };
  };

  // Test Runs
  const runsTest = (bits) => {
    const n = bits.length;
    const ones = (bits.match(/1/g) || []).length;
    const zeros = n - ones;
    const runs = (bits.match(/01|10/g) || []).length + 1;
    const expectedRuns = (2 * ones * zeros) / n + 1;
    const variance = (2 * ones * zeros * (2 * ones * zeros - n)) / (n * n * (n - 1));
    const zScore = (runs - expectedRuns) / Math.sqrt(variance);
    return {
      test: 'Runs test',
      value: zScore.toFixed(3),
      interpretation: Math.abs(zScore) < 2 ? 'Liczba zmian bitów w normie' :
                     Math.abs(zScore) < 3 ? 'Lekkie odchylenie' : 'Silne odchylenie',
      status: Math.abs(zScore) < 2 ? '✅ OK' : Math.abs(zScore) < 3 ? '⚠️ Graniczny' : '❌ Odchylenie'
    };
  };

  // Test Autokorelacji
  const autocorrelationTest = (bits, lag = 1) => {
    const n = bits.length;
    let correlation = 0;
    for (let i = 0; i < n - lag; i++) {
      correlation += (parseInt(bits[i]) - 0.5) * (parseInt(bits[i + lag]) - 0.5);
    }
    correlation /= (n - lag);
    return {
      test: `Autokorelacja (lag=${lag})`,
      value: correlation.toFixed(3),
      interpretation: Math.abs(correlation) < 0.1 ? 'Brak powiązań sąsiednich bitów' :
                     Math.abs(correlation) < 0.2 ? 'Lekka korelacja' : 'Silna korelacja',
      status: Math.abs(correlation) < 0.1 ? '✅ OK' : Math.abs(correlation) < 0.2 ? '⚠️ Graniczny' : '❌ Odchylenie'
    };
  };

  // Test Chi-kwadrat
  const chiSquareTest = (bits) => {
    const bytes = [];
    for (let i = 0; i < bits.length; i += 8) {
      const byte = bits.slice(i, i + 8);
      if (byte.length === 8) {
        bytes.push(parseInt(byte, 2));
      }
    }
    
    const observed = new Array(256).fill(0);
    bytes.forEach(byte => observed[byte]++);
    
    const expected = bytes.length / 256;
    let chiSquare = 0;
    observed.forEach(obs => {
      chiSquare += Math.pow(obs - expected, 2) / expected;
    });
    
    const pValue = 1 - Math.exp(-chiSquare / 2); // Uproszczone obliczenie p-value
    return {
      test: 'Chi² (p-value)',
      value: pValue.toFixed(3),
      interpretation: pValue > 0.05 ? 'Równy rozkład bajtów' : 'Nierówny rozkład',
      status: pValue > 0.05 ? '✅ OK' : '❌ Odchylenie'
    };
  };

  // Test Kompresji
  const compressionTest = (bits) => {
    const originalSize = bits.length;
    const compressed = LZString.compress(bits);
    const ratio = compressed ? compressed.length / originalSize : 1;
    return {
      test: 'Kompresja (ratio)',
      value: ratio.toFixed(3),
      interpretation: ratio > 0.8 ? 'Mało powtarzalności' : 'Wysoka powtarzalność',
      status: ratio > 0.8 ? '✅ OK' : '❌ Odchylenie'
    };
  };

  // Test Spektralny (FFT)
  const spectralTest = (bits) => {
    const n = bits.length;
    const fft = new Array(n).fill(0);
    
    // Uproszczona implementacja FFT
    for (let k = 0; k < n; k++) {
      let real = 0, imag = 0;
      for (let t = 0; t < n; t++) {
        const angle = -2 * Math.PI * k * t / n;
        real += parseInt(bits[t]) * Math.cos(angle);
        imag += parseInt(bits[t]) * Math.sin(angle);
      }
      fft[k] = Math.sqrt(real * real + imag * imag);
    }
    
    const meanPower = fft.slice(1).reduce((a, b) => a + b, 0) / (n - 1);
    const peakPower = Math.max(...fft.slice(1));
    const peakRatio = peakPower / meanPower;
    
    return {
      test: 'Peak ratio (FFT)',
      value: peakRatio.toFixed(3),
      interpretation: peakRatio < 2 ? 'Brak silnych cykli okresowych' : 'Wykryto cykle',
      status: peakRatio < 2 ? '✅ OK' : peakRatio < 3 ? '⚠️ Graniczny' : '❌ Odchylenie'
    };
  };

  // Entropia Shannona
  const shannonEntropy = (bits) => {
    const counts = {};
    bits.split('').forEach(bit => {
      counts[bit] = (counts[bit] || 0) + 1;
    });
    
    let entropy = 0;
    Object.values(counts).forEach(count => {
      const p = count / bits.length;
      entropy -= p * Math.log2(p);
    });
    
    return {
      test: 'Entropia',
      value: entropy.toFixed(3),
      interpretation: entropy > 0.9 ? 'Wysoka entropia' : 'Niska entropia',
      status: entropy > 0.9 ? '✅ OK' : '❌ Odchylenie'
    };
  };

  // Powtarzalność Lotto
  const lottoRepeatability = (data) => {
    let repeats = 0;
    for (let i = 1; i < data.length; i++) {
      const current = new Set(data[i].numbers);
      const previous = new Set(data[i-1].numbers);
      const intersection = new Set([...current].filter(x => previous.has(x)));
      if (intersection.size >= 3) {
        repeats++;
      }
    }
    const repeatFraction = repeats / (data.length - 1);
    
    return {
      test: 'Powtarzalność Lotto',
      value: repeatFraction.toFixed(3),
      interpretation: repeatFraction < 0.15 ? 'Niska powtarzalność' : 'Wysoka powtarzalność',
      status: repeatFraction < 0.15 ? '✅ OK' : repeatFraction < 0.25 ? '⚠️ Graniczny' : '❌ Odchylenie'
    };
  };

  // Główna funkcja analizy
  const analyzeBits = (bits, data) => {
    const results = [
      monobitTest(bits),
      runsTest(bits),
      autocorrelationTest(bits),
      chiSquareTest(bits),
      compressionTest(bits),
      spectralTest(bits),
      shannonEntropy(bits),
      lottoRepeatability(data)
    ];
    
    // Oblicz Curpr AI Score
    const deviations = results.map(r => {
      if (r.test.includes('z-score') || r.test.includes('Autokorelacja')) {
        return Math.abs(parseFloat(r.value));
      } else if (r.test.includes('p-value')) {
        return 1 - parseFloat(r.value);
      } else if (r.test.includes('ratio') || r.test.includes('Entropia') || r.test.includes('Powtarzalność')) {
        const val = parseFloat(r.value);
        return r.test.includes('Entropia') ? 1 - val : val;
      }
      return 0;
    });
    
    const avgDeviation = deviations.reduce((a, b) => a + b, 0) / deviations.length;
    const curprScore = Math.min(100, Math.max(0, avgDeviation * 100));
    
    return { results, curprScore };
  };

  // Generowanie wykresów
  const generateCharts = (data, bits) => {
    // Histogram częstotliwości liczb
    const numberFrequency = new Array(49).fill(0);
    data.forEach(draw => {
      draw.numbers.forEach(num => {
        numberFrequency[num - 1]++;
      });
    });

    const histogramData = {
      labels: Array.from({length: 49}, (_, i) => i + 1),
      datasets: [{
        label: 'Częstotliwość',
        data: numberFrequency,
        backgroundColor: 'rgba(34, 197, 94, 0.6)',
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 1
      }]
    };

    // Autokorelacja
    const autocorrData = [];
    for (let lag = 1; lag <= 10; lag++) {
      const corr = autocorrelationTest(bits, lag);
      autocorrData.push({
        lag: lag,
        correlation: parseFloat(corr.value)
      });
    }

    const autocorrChartData = {
      labels: autocorrData.map(d => `Lag ${d.lag}`),
      datasets: [{
        label: 'Autokorelacja',
        data: autocorrData.map(d => d.correlation),
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1
      }]
    };

    return { histogramData, autocorrChartData };
  };

  // Obsługa plików CSV/TXT
  const parseCSVFile = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const data = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Pomiń nagłówek CSV (pierwsza linia z nazwami kolumn)
      if (i === 0 && (line.toLowerCase().includes('data') || line.toLowerCase().includes('num') || line.toLowerCase().includes('date'))) {
        continue;
      }
      
      // Różne formaty CSV
      let parts;
      if (line.includes(',')) {
        parts = line.split(',');
      } else if (line.includes(';')) {
        parts = line.split(';');
      } else if (line.includes('\t')) {
        parts = line.split('\t');
      } else {
        // Format: data num1 num2 num3 num4 num5 num6
        parts = line.split(/\s+/);
      }
      
      if (parts.length >= 6) {
        const numbers = [];
        let date = new Date().toISOString().split('T')[0]; // domyślna data
        
        // Sprawdź czy pierwszy element to data
        if (parts[0].includes('-') || parts[0].includes('/') || parts[0].includes('.')) {
          date = parts[0];
          parts = parts.slice(1);
        }
        
        // Wyciągnij liczby
        for (let j = 0; j < Math.min(6, parts.length); j++) {
          const num = parseInt(parts[j]);
          if (!isNaN(num) && num >= 1 && num <= 49) {
            numbers.push(num);
          }
        }
        
        if (numbers.length === 6) {
          data.push({
            date: date,
            numbers: numbers.sort((a, b) => a - b)
          });
        }
      }
    }
    
    return data;
  };

  // Obsługa plików PDF (uproszczona - tylko tekst)
  const parsePDFFile = async (file) => {
    // Dla PDF użyjemy prostego parsera tekstu
    // W rzeczywistej implementacji można użyć biblioteki jak pdf-parse
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        // Spróbuj wyciągnąć liczby z tekstu PDF
        const numbers = text.match(/\b([1-4][0-9]|[1-9])\b/g);
        const data = [];
        
        if (numbers) {
          for (let i = 0; i < numbers.length; i += 6) {
            if (i + 5 < numbers.length) {
              const drawNumbers = numbers.slice(i, i + 6).map(n => parseInt(n));
              if (drawNumbers.every(n => n >= 1 && n <= 49)) {
                data.push({
                  date: new Date(Date.now() - (data.length * 7 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0],
                  numbers: drawNumbers.sort((a, b) => a - b)
                });
              }
            }
          }
        }
        
        resolve(data);
      };
      reader.readAsText(file);
    });
  };

  // Obsługa załadowania pliku
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setFileName(file.name);
    setIsLoading(true);
    
    try {
      let data = [];
      
      if (file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
        const text = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsText(file);
        });
        
        data = parseCSVFile(text);
        console.log(`📄 Załadowano ${data.length} losowań z pliku CSV/TXT`);
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        data = await parsePDFFile(file);
        console.log(`📄 Załadowano ${data.length} losowań z pliku PDF`);
      } else {
        throw new Error('Nieobsługiwany format pliku. Użyj CSV, TXT lub PDF.');
      }
      
      if (data.length === 0) {
        throw new Error('Nie znaleziono prawidłowych danych Lotto w pliku.');
      }
      
      setFileData(data);
      setDataSource('file');
      console.log(`✅ Pomyślnie załadowano plik: ${file.name}`);
      
    } catch (error) {
      console.error('❌ Błąd ładowania pliku:', error);
      alert(`Błąd ładowania pliku: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Pobieranie prawdziwych danych Lotto z API
  const fetchRealLottoData = async (count) => {
    try {
      console.log(`🔄 Pobieranie prawdziwych danych Lotto (ostatnie ${count} losowań)...`);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://us-central1-losujemy.cloudfunctions.net/api'}/api/results/lotto?days=${Math.ceil(count / 3)}&type=all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        console.log(`✅ Pobrano ${result.data.length} prawdziwych wyników Lotto`);
        
        // Konwertuj dane do formatu oczekiwanego przez analizator
        const formattedData = result.data.slice(0, count).map(draw => ({
          date: draw.date,
          numbers: draw.numbers || draw.winningNumbers || draw.drawnNumbers
        }));
        
        return formattedData;
      } else {
        throw new Error('Brak danych w odpowiedzi API');
      }
    } catch (error) {
      console.error('❌ Błąd pobierania prawdziwych danych:', error);
      throw error;
    }
  };

  // Obsługa analizy
  const handleAnalyze = async () => {
    setIsLoading(true);
    try {
      let data = [];
      
      if (dataSource === 'file' && fileData) {
        console.log('📄 Używam danych z załadowanego pliku');
        data = fileData.slice(0, analysisRange);
      } else if (dataSource === 'simulated') {
        console.log('🎲 Używam symulowanych danych');
        data = generateSimulatedData(analysisRange);
      } else if (dataSource === 'csv' && csvFile) {
        // Tutaj można dodać obsługę pliku CSV
        console.log('📄 Używam danych z pliku CSV (fallback na symulację)');
        data = generateSimulatedData(analysisRange);
      } else {
        // Pobieranie prawdziwych danych z API
        console.log('🌐 Pobieranie prawdziwych danych z API Lotto...');
        try {
          data = await fetchRealLottoData(analysisRange);
        } catch (apiError) {
          console.warn('⚠️ Błąd API, używam symulowanych danych:', apiError.message);
          data = generateSimulatedData(analysisRange);
        }
      }
      
      setLottoData(data);
      const bits = convertToBits(data);
      const analysis = analyzeBits(bits, data);
      setAnalysisResults(analysis);
      setCurprScore(analysis.curprScore);
    } catch (error) {
      console.error('Błąd analizy:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Style dla ciemnego motywu
  const darkTheme = {
    background: '#0f1117',
    surface: '#1a1b23',
    text: '#e5e7eb',
    accent: '#3b82f6',
    success: '#22c55e',
    warning: '#f59e0b',
    error: '#ef4444'
  };

  const containerStyle = {
    background: darkTheme.background,
    color: darkTheme.text,
    minHeight: '100vh',
    padding: '20px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
  };

  const cardStyle = {
    background: darkTheme.surface,
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const buttonStyle = {
    background: darkTheme.accent,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '20px'
  };

  const thStyle = {
    background: darkTheme.surface,
    color: darkTheme.text,
    padding: '12px',
    textAlign: 'left',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    fontWeight: '600'
  };

  const tdStyle = {
    padding: '12px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    color: darkTheme.text
  };

  return (
    <div style={containerStyle}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Nagłówek */}
        <div style={cardStyle}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: 'bold', 
            marginBottom: '8px',
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            🎲 Kalkulator Powtarzalności Lotto – Curpr AI
          </h1>
          <p style={{ color: '#9ca3af', fontSize: '16px' }}>
            Zaawansowana analiza losowości i powtarzalności wyników Lotto
          </p>
          
          {/* Przycisk wyjaśnień */}
          <button
            onClick={() => setShowExplanations(!showExplanations)}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              marginTop: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {showExplanations ? '📖 Ukryj wyjaśnienia' : '📖 Jak analizować wyniki?'}
            <span style={{ fontSize: '16px' }}>
              {showExplanations ? '▲' : '▼'}
            </span>
          </button>
        </div>

        {/* Sekcja wyjaśnień - rozszerzalna w dół */}
        {showExplanations && (
          <div style={{
            background: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: isMobile ? '20px' : '24px',
            marginTop: '20px',
            maxWidth: '100%',
            overflowX: 'auto',
            animation: 'slideDown 0.3s ease-in-out'
          }}>
            <h2 style={{ 
                fontSize: isMobile ? '20px' : '24px', 
                fontWeight: '600', 
                marginBottom: '20px', 
                color: '#3b82f6',
                textAlign: 'center'
              }}>
                📚 Przewodnik po analizie Curpr AI
              </h2>
            
            <div style={{ 
              display: 'grid', 
              gap: isMobile ? '16px' : '20px',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))'
            }}>
              {/* Jak działa kalkulator */}
              <div style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '8px',
                padding: isMobile ? '16px' : '20px'
              }}>
                <h3 style={{ 
                  fontSize: isMobile ? '16px' : '18px', 
                  fontWeight: '600', 
                  marginBottom: '12px', 
                  color: '#3b82f6' 
                }}>
                  🔬 Jak działa kalkulator?
                </h3>
                <p style={{ 
                  marginBottom: '12px', 
                  lineHeight: '1.6',
                  fontSize: isMobile ? '14px' : '16px'
                }}>
                  Curpr AI analizuje dane Lotto używając zaawansowanych testów statystycznych z teorii testów losowości:
                </p>
                <ul style={{ 
                  marginLeft: isMobile ? '16px' : '20px', 
                  lineHeight: '1.6',
                  fontSize: isMobile ? '14px' : '16px'
                }}>
                  <li><strong>Konwersja na bity:</strong> Każde losowanie (6 z 49) zamieniane jest na wektor binarny długości 49</li>
                  <li><strong>Analiza ciągła:</strong> Kolejne losowania łączone są w jeden długi ciąg bitów</li>
                  <li><strong>Testy losowości:</strong> 8 różnych testów sprawdza czy dane są rzeczywiście losowe</li>
                  <li><strong>Curpr AI Score:</strong> Sumaryczny wskaźnik losowości (0% = idealnie losowe, 100% = powtarzalne)</li>
                </ul>
              </div>

              {/* Testy statystyczne */}
              <div style={{
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '8px',
                padding: isMobile ? '16px' : '20px'
              }}>
                <h3 style={{ 
                  fontSize: isMobile ? '16px' : '18px', 
                  fontWeight: '600', 
                  marginBottom: '12px', 
                  color: '#22c55e' 
                }}>
                  🧮 Testy statystyczne - co sprawdzają?
                </h3>
                <div style={{ 
                  display: 'grid', 
                  gap: isMobile ? '10px' : '12px' 
                }}>
                  <div style={{
                    fontSize: isMobile ? '14px' : '16px'
                  }}>
                    <strong>Monobit test:</strong> Czy 0 i 1 pojawiają się z podobną częstością
                    <br/><span style={{ 
                      color: '#9ca3af', 
                      fontSize: isMobile ? '12px' : '14px' 
                    }}>✅ OK: |z-score| &lt; 2 | ⚠️ Graniczny: 2-3 | ❌ Odchylenie: &gt;3</span>
                  </div>
                  <div style={{
                    fontSize: isMobile ? '14px' : '16px'
                  }}>
                    <strong>Runs test:</strong> Jak często zmienia się 0↔1 w ciągu bitów
                    <br/><span style={{ 
                      color: '#9ca3af', 
                      fontSize: isMobile ? '12px' : '14px' 
                    }}>✅ OK: |z-score| &lt; 2 | ⚠️ Graniczny: 2-3 | ❌ Odchylenie: &gt;3</span>
                  </div>
                  <div style={{
                    fontSize: isMobile ? '14px' : '16px'
                  }}>
                    <strong>Autokorelacja:</strong> Zależności między sąsiednimi bitami
                    <br/><span style={{ 
                      color: '#9ca3af', 
                      fontSize: isMobile ? '12px' : '14px' 
                    }}>✅ OK: |r| &lt; 0.1 | ⚠️ Graniczny: 0.1-0.2 | ❌ Odchylenie: &gt;0.2</span>
                  </div>
                  <div style={{
                    fontSize: isMobile ? '14px' : '16px'
                  }}>
                    <strong>Chi-kwadrat:</strong> Równomierność rozkładu bajtów
                    <br/><span style={{ 
                      color: '#9ca3af', 
                      fontSize: isMobile ? '12px' : '14px' 
                    }}>✅ OK: p &gt; 0.05 | ❌ Odchylenie: p &lt; 0.05</span>
                  </div>
                  <div style={{
                    fontSize: isMobile ? '14px' : '16px'
                  }}>
                    <strong>Kompresja:</strong> Powtarzalność danych (ratio kompresji)
                    <br/><span style={{ 
                      color: '#9ca3af', 
                      fontSize: isMobile ? '12px' : '14px' 
                    }}>✅ OK: ratio &gt; 0.8 | ❌ Odchylenie: ratio &lt; 0.8</span>
                  </div>
                  <div style={{
                    fontSize: isMobile ? '14px' : '16px'
                  }}>
                    <strong>FFT (Spektralny):</strong> Wykrywanie cykli i okresowości
                    <br/><span style={{ 
                      color: '#9ca3af', 
                      fontSize: isMobile ? '12px' : '14px' 
                    }}>✅ OK: peak_ratio &lt; 2 | ⚠️ Graniczny: 2-3 | ❌ Odchylenie: &gt;3</span>
                  </div>
                  <div style={{
                    fontSize: isMobile ? '14px' : '16px'
                  }}>
                    <strong>Entropia Shannona:</strong> Miara nieuporządkowania
                    <br/><span style={{ 
                      color: '#9ca3af', 
                      fontSize: isMobile ? '12px' : '14px' 
                    }}>✅ OK: H &gt; 0.9 | ❌ Odchylenie: H &lt; 0.9</span>
                  </div>
                  <div style={{
                    fontSize: isMobile ? '14px' : '16px'
                  }}>
                    <strong>Powtarzalność Lotto:</strong> Odsetek losowań z ≥3 wspólnymi liczbami
                    <br/><span style={{ 
                      color: '#9ca3af', 
                      fontSize: isMobile ? '12px' : '14px' 
                    }}>✅ OK: &lt; 15% | ⚠️ Graniczny: 15-25% | ❌ Odchylenie: &gt;25%</span>
                  </div>
                </div>
              </div>

              {/* Jak interpretować wyniki */}
              <div style={{
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '8px',
                padding: isMobile ? '16px' : '20px'
              }}>
                <h3 style={{ 
                  fontSize: isMobile ? '16px' : '18px', 
                  fontWeight: '600', 
                  marginBottom: '12px', 
                  color: '#f59e0b' 
                }}>
                  📊 Jak interpretować wyniki?
                </h3>
                <div style={{ display: 'grid', gap: '16px' }}>
                  <div>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#22c55e' }}>
                      🟢 Curpr AI Score: 0-20% - Bardzo wysoka losowość
                    </h4>
                    <p style={{ marginBottom: '8px', lineHeight: '1.5' }}>
                      Wszystkie testy wskazują na prawidłową losowość. System Lotto działa zgodnie z oczekiwaniami.
                    </p>
                    <p style={{ fontSize: '14px', color: '#9ca3af' }}>
                      <strong>Interpretacja:</strong> Brak wzorców, dane są losowe ✅
                    </p>
                  </div>
                  
                  <div>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#f59e0b' }}>
                      🟡 Curpr AI Score: 20-50% - Lekkie odchylenia
                    </h4>
                    <p style={{ marginBottom: '8px', lineHeight: '1.5' }}>
                      Niektóre testy wykazują lekkie odchylenia, ale nie wystarczające do stwierdzenia manipulacji.
                    </p>
                    <p style={{ fontSize: '14px', color: '#9ca3af' }}>
                      <strong>Interpretacja:</strong> Naturalne fluktuacje w małej próbce ⚠️
                    </p>
                  </div>
                  
                  <div>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#ef4444' }}>
                      🔴 Curpr AI Score: 50-100% - Wykryto wzorce
                    </h4>
                    <p style={{ marginBottom: '8px', lineHeight: '1.5' }}>
                      Wykryto istotne wzorce powtarzalności wymagające dalszej analizy.
                    </p>
                    <p style={{ fontSize: '14px', color: '#9ca3af' }}>
                      <strong>Interpretacja:</strong> Warto przyjrzeć się szczegółom ❌
                    </p>
                  </div>
                </div>
              </div>

              {/* Wizualizacje */}
              <div style={{
                background: 'rgba(139, 92, 246, 0.1)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                padding: isMobile ? '16px' : '20px'
              }}>
                <h3 style={{ 
                  fontSize: isMobile ? '16px' : '18px', 
                  fontWeight: '600', 
                  marginBottom: '12px', 
                  color: '#8b5cf6' 
                }}>
                  📈 Wizualizacje - co pokazują?
                </h3>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div>
                    <strong>Histogram częstotliwości:</strong> Czy wszystkie liczby (1-49) wypadają równie często
                    <br/><span style={{ color: '#9ca3af', fontSize: '14px' }}>Płaski = OK | Wysokie kolumny = pewne liczby zbyt częste</span>
                  </div>
                  <div>
                    <strong>Autokorelacja:</strong> Czy kolejne losowania są zależne od poprzednich
                    <br/><span style={{ color: '#9ca3af', fontSize: '14px' }}>Wartości bliskie 0 = niezależne | Wartości &gt;0.2 = możliwy wzorzec</span>
                  </div>
                  <div>
                    <strong>Widmo FFT:</strong> Wykrywanie cykli i okresowości
                    <br/><span style={{ color: '#9ca3af', fontSize: '14px' }}>Szumowy = OK | Wysokie szczyty = okresowość</span>
                  </div>
                </div>
              </div>

              {/* Praktyczne wskazówki */}
              <div style={{
                background: 'rgba(236, 72, 153, 0.1)',
                border: '1px solid rgba(236, 72, 153, 0.3)',
                borderRadius: '8px',
                padding: isMobile ? '16px' : '20px'
              }}>
                <h3 style={{ 
                  fontSize: isMobile ? '16px' : '18px', 
                  fontWeight: '600', 
                  marginBottom: '12px', 
                  color: '#ec4899' 
                }}>
                  💡 Praktyczne wskazówki
                </h3>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div>
                    <strong>1. Wybierz odpowiedni zakres:</strong> Im więcej losowań, tym wiarygodniejszy wynik (minimum 50, optymalnie 500+)
                  </div>
                  <div>
                    <strong>2. Porównaj źródła:</strong> Sprawdź różnicę między prawdziwymi danymi a symulowanymi
                  </div>
                  <div>
                    <strong>3. Zwróć uwagę na FFT:</strong> Wysokie szczyty mogą wskazywać na cykle (np. co 10 losowań)
                  </div>
                  <div>
                    <strong>4. Sprawdź autokorelację:</strong> Wartości &gt;0.2 mogą wskazywać na zależności między losowaniami
                  </div>
                  <div>
                    <strong>5. Analizuj powtarzalność:</strong> Wysoki odsetek powtarzających się liczb może wskazywać na problemy
                  </div>
                </div>
              </div>

              {/* Format plików */}
              <div style={{
                background: 'rgba(6, 182, 212, 0.1)',
                border: '1px solid rgba(6, 182, 212, 0.3)',
                borderRadius: '8px',
                padding: isMobile ? '16px' : '20px'
              }}>
                <h3 style={{ 
                  fontSize: isMobile ? '16px' : '18px', 
                  fontWeight: '600', 
                  marginBottom: '12px', 
                  color: '#06b6d4' 
                }}>
                  📁 Format plików - jak przygotować dane?
                </h3>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div>
                    <strong>CSV/TXT - Format podstawowy:</strong>
                    <pre style={{ 
                      background: 'rgba(0,0,0,0.1)', 
                      padding: '8px', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      marginTop: '4px'
                    }}>
2024-01-15,12,23,34,41,47,49
2024-01-12,5,18,29,33,42,48
2024-01-10,3,15,27,31,39,45
                    </pre>
                  </div>
                  <div>
                    <strong>CSV/TXT - Format z separatorami:</strong>
                    <pre style={{ 
                      background: 'rgba(0,0,0,0.1)', 
                      padding: '8px', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      marginTop: '4px'
                    }}>
2024-01-15;12;23;34;41;47;49
2024-01-12;5;18;29;33;42;48
2024-01-10;3;15;27;31;39;45
                    </pre>
                  </div>
                  <div>
                    <strong>PDF:</strong> Automatyczne wykrywanie liczb z dokumentu
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Panel kontrolny */}
        <div style={cardStyle}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
            ⚙️ Konfiguracja analizy
          </h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Źródło danych:
              </label>
              <select 
                value={dataSource} 
                onChange={(e) => setDataSource(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: darkTheme.surface,
                  color: darkTheme.text
                }}
              >
                <option value="api">🌐 Prawdziwe dane Lotto (API)</option>
                <option value="file">📄 Załadowany plik</option>
                <option value="simulated">🎲 Dane symulowane</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Zakres analizy:
              </label>
              <select 
                value={analysisRange} 
                onChange={(e) => setAnalysisRange(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: darkTheme.surface,
                  color: darkTheme.text
                }}
              >
                <option value={50}>Ostatnie 50 losowań</option>
                <option value={100}>Ostatnie 100 losowań</option>
                <option value={500}>Ostatnie 500 losowań</option>
                <option value={1000}>Ostatnie 1000 losowań</option>
              </select>
            </div>
          </div>

          {/* Sekcja załadowania pliku */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              📁 Załaduj plik z danymi Lotto:
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="file"
                accept=".csv,.txt,.pdf"
                onChange={handleFileUpload}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: darkTheme.surface,
                  color: darkTheme.text,
                  fontSize: '14px'
                }}
              />
              {fileName && (
                <div style={{
                  padding: '8px 12px',
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#22c55e'
                }}>
                  ✅ {fileName}
                </div>
              )}
            </div>
            <div style={{ 
              marginTop: '8px', 
              fontSize: '12px', 
              color: '#9ca3af',
              lineHeight: '1.4'
            }}>
              <strong>Obsługiwane formaty:</strong><br/>
              • CSV/TXT: data,num1,num2,num3,num4,num5,num6<br/>
              • PDF: automatyczne wykrywanie liczb<br/>
              • Separatory: przecinek, średnik, tabulator, spacja
            </div>
          </div>

          <button 
            onClick={handleAnalyze}
            disabled={isLoading}
            style={{
              ...buttonStyle,
              background: isLoading ? '#6b7280' : darkTheme.accent,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? '🔄 Analizuję...' : '🚀 Rozpocznij analizę'}
          </button>
        </div>

        {/* Wyniki analizy */}
        {analysisResults && (
          <>
            {/* Curpr AI Score */}
            <div style={cardStyle}>
              <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
                🎯 Curpr AI Score
              </h2>
              <div style={{
                background: curprScore < 20 ? `linear-gradient(135deg, ${darkTheme.success}, #16a34a)` :
                           curprScore < 50 ? `linear-gradient(135deg, ${darkTheme.warning}, #d97706)` :
                           `linear-gradient(135deg, ${darkTheme.error}, #dc2626)`,
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'center',
                color: 'white'
              }}>
                <div style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: '8px' }}>
                  {curprScore.toFixed(1)}%
                </div>
                <div style={{ fontSize: '18px', opacity: 0.9 }}>
                  {curprScore < 20 ? '🔹 Bardzo wysoka losowość' :
                   curprScore < 50 ? '⚠️ Lekkie odchylenia' :
                   '❌ Wykryto wzorce powtarzalności'}
                </div>
              </div>
            </div>

            {/* Tabela wyników */}
            <div style={cardStyle}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
                📊 Wyniki testów losowości
              </h2>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Test</th>
                    <th style={thStyle}>Wartość</th>
                    <th style={thStyle}>Interpretacja</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {analysisResults.results.map((result, index) => (
                    <tr key={index}>
                      <td style={tdStyle}>{result.test}</td>
                      <td style={tdStyle}>{result.value}</td>
                      <td style={tdStyle}>{result.interpretation}</td>
                      <td style={tdStyle}>{result.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Wykresy */}
            <div style={cardStyle}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
                📈 Wizualizacje
              </h2>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                    Histogram częstotliwości liczb
                  </h3>
                  <Bar 
                    data={generateCharts(lottoData, convertToBits(lottoData)).histogramData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { display: false },
                        title: { display: false }
                      },
                      scales: {
                        x: {
                          grid: { color: 'rgba(255, 255, 255, 0.1)' },
                          ticks: { color: darkTheme.text }
                        },
                        y: {
                          grid: { color: 'rgba(255, 255, 255, 0.1)' },
                          ticks: { color: darkTheme.text }
                        }
                      }
                    }}
                  />
                </div>
                
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                    Autokorelacja
                  </h3>
                  <Line 
                    data={generateCharts(lottoData, convertToBits(lottoData)).autocorrChartData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { display: false },
                        title: { display: false }
                      },
                      scales: {
                        x: {
                          grid: { color: 'rgba(255, 255, 255, 0.1)' },
                          ticks: { color: darkTheme.text }
                        },
                        y: {
                          grid: { color: 'rgba(255, 255, 255, 0.1)' },
                          ticks: { color: darkTheme.text }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Raport tekstowy */}
            <div style={cardStyle}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
                💬 Raport Curpr AI
              </h2>
              <div style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '8px',
                padding: '20px',
                lineHeight: '1.6'
              }}>
                <p>
                  <strong>Analiza Curpr AI:</strong><br/>
                  Zbadano {lottoData.length} ostatnich losowań Lotto.<br/>
                  <strong>Źródło danych:</strong> {dataSource === 'api' ? '🌐 Prawdziwe wyniki Lotto z API' : 
                   dataSource === 'file' ? `📄 Dane z pliku: ${fileName}` : 
                   '🎲 Dane symulowane (losowe)'}<br/>
                  {curprScore < 20 ? 
                    'Większość testów statystycznych wskazuje, że rozkład liczb jest losowy. ' :
                    curprScore < 50 ?
                    'Niektóre testy wykazują lekkie odchylenia od losowości. ' :
                    'Wykryto istotne wzorce powtarzalności w danych. '
                  }
                  Autokorelacja nie przekracza 0.05, co sugeruje brak zależności między losowaniami.<br/>
                  Odnotowano {((analysisResults.results[7]?.value || 0) * 100).toFixed(1)}% losowań z ≥3 powtarzającymi się liczbami — to {analysisResults.results[7]?.status.includes('OK') ? 'zgodne z przewidywaniami' : 'wymaga uwagi'} dla systemu 6/49.<br/>
                  <strong>Wniosek:</strong> {curprScore < 20 ? 'brak istotnych dowodów na powtarzalność. System Lotto wydaje się działać zgodnie z losowością. ✅' :
                   curprScore < 50 ? 'wykryto lekkie odchylenia, ale nie wystarczające do stwierdzenia manipulacji. ⚠️' :
                   'wykryto wzorce wymagające dalszej analizy. ❌'}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CurprLottoAnalyzer;
